import _ from "lodash";
import { NoteRangeType } from "./containers";
import { assert } from "../utils/error";
import { ChordStructure } from "./chords";
import { Range } from "../utils/types/Range";


const __DEFAULT_NOTE_RANGES: { notes: NoteRangeType, tones: NoteRangeType } = {
    notes: { min: 0, max: 107 },
    tones: { min: 0, max: 12 }
};

export const NOTES_DOMAIN = { min: 0, max: 108 };

// FIXME: this should go only up to 8
export type OctaveIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type ToneIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
class Tone {
    private static TONE_NAMES = [
        "C",
        "C#",
        "D",
        "D#",
        "E",
        "F",
        "F#",
        "G",
        "G#",
        "A",
        "A#",
        "B",
    ];

    private static TONES = this.TONE_NAMES.map((name, index) => new Tone(index as ToneIndex));

    static isValid(name: string): boolean {
        return this.TONE_NAMES.includes(name);
    }

    static all(): Tone[] {
        return Tone.TONES.map(t => t);
    }

    // readonly index: ToneIndex;
    private constructor(readonly index: ToneIndex) {
        // if (index < 0 || index >= Tone.TONE_NAMES.length) {
        //     throw new Error(`Invalid tone index. Was: ${index}`);
        // }
        // this.index = index;
    }

    static fromName(name: string): Tone {
        return Tone.TONES[Tone.iFromName(name)];
    }

    static fromIndex(index: ToneIndex): Tone {
        return Tone.TONES[index];
    }

    static iFromName(name: string): ToneIndex {
        const index = Tone.TONE_NAMES.indexOf(name);
        if (index === -1) {
            throw new Error(`Invalid note name. Was: ${name}`);
        }
        return index as ToneIndex;
    }

    getName(): string {
        return Tone.TONE_NAMES[this.index];
    }

    toNote(o: OctaveIndex): Note {
        return Note.fromIndex(this.index).up(o * 12);
    }

    up(steps: number): Tone {
        return new Tone((this.index + steps) % 12 as ToneIndex);
    }
    down(steps: number): Tone {
        return new Tone((this.index - steps + 12) % 12 as ToneIndex);
    }
}

class Note {
    private static NOTE_NAMES: string[] = Array(9).fill(0).map((_, octave) =>
        ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'].map((note) => `${note}${octave}`))
        .flat()
        .concat(['C9']); // lazy me

    private static NOTES = Note.NOTE_NAMES.map((name, index) => new Note(index));
    static MAX_NOTE_INDEX = Note.NOTE_NAMES.length - 1;

    static all(): Note[] {
        return Note.NOTES.map(n => n);
    }

    //  index: number;
    private constructor(readonly index: number) {
        if (index < 0 || index >= Note.NOTE_NAMES.length) {
            throw new Error(`Invalid note index. Was: ${index}`);
        }
        // this.index = index;
    }

    static isValid(name: string): boolean {
        return this.NOTE_NAMES.includes(name);
    }

    static fromName(name: string): Note {
        return Note.NOTES[Note.iFromName(name)];
    }

    static toTone(note: Tone | Note): any {
        return Tone.fromIndex(note.index % 12 as ToneIndex);
    }

    static iFromName(name: string): number {
        const index = Note.NOTE_NAMES.indexOf(name);
        if (index === -1) {
            throw new Error(`Invalid note name. Was: ${name}`);
        }
        return index;
    }

    static tryFromName(name: string): Note | undefined {
        const index = Note.NOTE_NAMES.indexOf(name);
        if (index === -1) {
            return undefined;
        }
        return Note.NOTES[index];
    }

    static fromIndex(index: number): Note {
        return Note.NOTES[index];
    }

    getName(): string {
        return Note.NOTE_NAMES[this.index];
    }

    getTone(): Tone {
        return Note.toTone(this);
    }

    up(steps: number): Note {
        const newIndex = this.index + steps;
        return Note.fromIndex(newIndex);
    }

    down(steps: number): Note {
        const newIndex = this.index - steps;
        return Note.fromIndex(newIndex);
    }
}

class NoteDuration {

    value: number;

    constructor(value: number) {
        if (!Number.isInteger(value)) {
            throw new Error(`Duration must be an integer. Was: ${value}`);
        }
        this.value = value;
    }

    static durationMap = {
        DOUBLE_DOTTED_DOUBLE_WHOLE: 224,
        DOTTED_DOUBLE_WHOLE: 192,
        DOUBLE_WHOLE: 128,
        DOUBLE_DOTTED_WHOLE: 112,
        DOTTED_WHOLE: 96,
        WHOLE: 64,
        DOUBLE_DOTTED_HALF: 56,
        DOTTED_HALF: 48,
        HALF: 32,
        DOUBLE_DOTTED_QUARTER: 28,
        DOTTED_QUARTER: 24,
        QUARTER: 16,
        DOUBLE_DOTTED_EIGHTH: 14,
        DOTTED_EIGHTH: 12,
        EIGHTH: 8,
        DOUBLE_DOTTED_SIXTEENTH: 7,
        DOTTED_SIXTEENTH: 6,
        SIXTEENTH: 4,
        DOUBLE_DOTTED_THIRTY_SECOND: 3.5,
        DOTTED_THIRTY_SECOND: 3,
        THIRTY_SECOND: 2,
        DOUBLE_DOTTED_SIXTY_FOURTH: 1.75,
        DOTTED_SIXTY_FOURTH: 1.5,
        SIXTY_FOURTH: 1
    };

    static fromBackendValue(value: string): NoteDuration {
        if (this.durationMap.hasOwnProperty(value)) {
            return new NoteDuration(this.durationMap[value]);
        } else {
            throw new Error(`Invalid duration value. Was: ${value}`);
        }
    }

    static fromTimeSignature(
        value: number,
        ppq: number = 16
    ) {
        switch (value) {

            case 1:
                return new NoteDuration((64 / 16) * ppq)
            case 2:
                return new NoteDuration((32 / 16) * ppq)
            case 4:
                return new NoteDuration((16 / 16) * ppq);
            case 8:
                return new NoteDuration((8 / 16) * ppq);
            case 16:
                return new NoteDuration((4 / 16) * ppq);
            case 32:
                return new NoteDuration((2 / 16) * ppq);
            case 64:
                return new NoteDuration((1 / 16) * ppq);
            default:
                throw new Error(`Invalid time signature denominator value. Was: ${value}`);
        }
    }

    toTimeSignature(): number {
        return NoteDuration.fromTimeSignature(this.value).value;
    }

    toToneJSValue(): string {
        return `${this.toTimeSignature()}n`;
    }

    getDurationValue(): number {
        return this.value;
    }
    getDurationName(): string {
        const found = _.toPairs(NoteDuration.durationMap).find(([key, val]) => {
            return val === this.value;
        });
        if (!found) {
            throw new Error(`Duration value ${this.value} not found in durationMap.`);
        }
        return found[0];
    }
    asCountedIn(duration: NoteDuration): number {
        return this.value / duration.value;
    }
}

export type NoteVelocity = Range<0, 128>;

class Chord {
    type?: string;
    root: Tone;
    family: string;
    tones: Tone[];

    constructor(root: Tone | string, family: string) {

        assert(root, 'Chord root is required');
        assert(family, 'Chord family is required');

        this.type = 'concrete';

        if (typeof root === 'string') {
            this.root = Tone.fromName(root);
        } else if (root instanceof Tone) {
            this.root = root;
        } else {
            throw new Error(`Invalid root type. Was: ${root}`);
        }

        this.family = family;
    }

    getName(): string {
        return `${this.root.getName()} ${this.family}`;
    }
}


export type HarmonicFieldPatterns = ChordStructure[][];
export type ChordTones = Tone[];

class MetronomeMarking {
    bpm: number;
    beatDuration: NoteDuration;

    constructor(bpm: number, beatDuration: NoteDuration) {
        this.bpm = bpm;
        this.beatDuration = beatDuration;
    }

    toString(): string {
        return `Q = ${this.bpm} BPM`;
    }
}

class TimeSignature {
    numerator: number;
    denominator: number;
    constructor(numerator: number, denominator: number) {
        this.numerator = numerator;
        this.denominator = denominator;
    }

    toString(): string {
        return `${this.numerator}/${this.denominator}`;
    }
}

export {
    Tone,
    Note,
    NoteDuration,
    Chord,
    __DEFAULT_NOTE_RANGES,
    MetronomeMarking,
    TimeSignature
};